//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import { Alert } from '@components/atoms/alert/Alert';
import { DefaultIconButton } from '@components/atoms/button/Button';
import Label from '@components/atoms/label/Label';
import {
  LabelText,
  MetatagText,
  SectionText,
} from '@components/atoms/text/Text';
import {
  SearchBarWithTypeAhead,
  SearchBarWithTypeAheadStateController,
} from '@components/molecules/search-bar-with-type-ahead/SearchBarWithTypeAhead';
import { KEYS } from '@constants/keyboard';
import Cuid from '@models/Cuid';
import { useModeratorUiaAccess } from '@moderation-mode/hooks/useModeratorUiaAccess';
import { hasPermissionForTask } from '@services/permissions';
import { useMediaTrackingContext } from '@tracker/mediaTracking/contexts/MediaTrackingContext';
import { MediaTrackingEventName } from '@tracker/mediaTracking/mediaTrackingModel';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { useSearchLabelsAutocompleteQuery } from '@uia/labels/hooks/useSearchLabelsAutocompleteQuery';
import { useUpdateMediaItemTagsMutation } from '@uia/labels/hooks/useUpdateMediaItemTagsMutation';
import { getSanitizedTags, isValidTag } from '@uia/labels/tagger/TagUtils';
import { isDefined } from '@utils/filterPredicates';
import { keyEquals } from '@utils/keyboardUtils';
import { pluralize } from '@utils/stringUtils';

const MAX_LABEL_LENGTH = 40;
const UIA_TAGGING_PERMISSION = 'universal_image_actions:tagging';

export interface LabelTaggerProps {
  cuid: Cuid;
  initialTags?: string[];
}

export const LabelTagger = ({ cuid, initialTags = [] }: LabelTaggerProps) => {
  const { isModeratorUiaEnabled } = useModeratorUiaAccess();
  const hasPermission = hasPermissionForTask(UIA_TAGGING_PERMISSION);
  const [tags, setTags] = useState<string[]>(initialTags);
  const [hasError, setHasError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef<SearchBarWithTypeAheadStateController>(null);

  const { data: response, isFetching } =
    useSearchLabelsAutocompleteQuery(searchQuery);
  const { isPending, mutate } = useUpdateMediaItemTagsMutation();

  const { trackEvent } = useMediaTrackingContext();

  const createTag = (input: string) => {
    if (!hasPermission || isPending) {
      return {
        shouldResetSearch: false,
        shouldClosePopover: false,
      };
    }

    if (isValidTag(input)) {
      const sanitizedTags = getSanitizedTags(input);
      const previousTags = tags;
      const newTags = Array.from(new Set([...tags, ...sanitizedTags]));
      const addedLabels = sanitizedTags.filter((tag) => !tags.includes(tag));
      setTags(newTags);
      trackEvent({
        cuid,
        event: MediaTrackingEventName.LabelAdded,
      });
      mutate(
        {
          cuid,
          isModeration: isModeratorUiaEnabled,
          labelsToAdd: addedLabels,
          request: { tags: newTags },
        },
        { onError: () => setTags(previousTags) },
      );
      setSearchQuery('');
      return { shouldResetSearch: true, shouldClosePopover: true };
    } else {
      setHasError(true);
      return { shouldClosePopover: true };
    }
  };

  const isDisabled =
    searchQuery.length === 0 || isPending || !isValidTag(searchQuery);

  return (
    <Tagger>
      <SectionText>Add Labels</SectionText>
      <TagWrapper>
        <LabelWrapper>
          <LabelText>Labels help you organize your content</LabelText>
        </LabelWrapper>
        {hasPermission ? (
          <Search>
            <SearchBarWithTypeAhead
              getTypeAheadResults={async (query) => setSearchQuery(query ?? '')}
              isLoadingTypeAheadResults={isFetching}
              isStatic={true}
              onKeyUp={(event, searchQuery) => {
                if (keyEquals(event, KEYS.ENTER)) {
                  setHasError(false);
                  return createTag(searchQuery);
                } else {
                  const hasValidInput =
                    isValidTag(searchQuery) || searchQuery.length === 0;
                  setHasError(!hasValidInput);
                  return {
                    shouldOpenPopover: hasValidInput,
                    shouldClosePopover: !hasValidInput,
                  };
                }
              }}
              onSearchSelection={async ({ label }) => {
                createTag(label);
              }}
              ref={searchRef}
              searchBarPlaceholderText='Filter labels that are already used'
              searchEmptyLabel='Start typing to find existing labels or add new ones'
              searchPrefixes={[]}
              shouldEnableSearchTermFiltering={false}
              shouldShowHeader={false}
              trackingIdPrefix={UIATrackingIds.LABELS_CREATE_TAG}
              typeAheadResults={(() => {
                const labels = response?.labels;

                if (searchQuery.length === 0) {
                  return [];
                }

                const typeAheadLabels = isDefined(labels)
                  ? Object.entries(labels).map(([label, count]) => ({
                      header: '',
                      count,
                      label,
                    }))
                  : [];

                // return the typeAheadLabels and the sanitized tags computed from the searchQuery that do not match any of the typeAheadLabels
                return [
                  ...typeAheadLabels,
                  ...getSanitizedTags(searchQuery)
                    .map((label) => ({
                      header: '',
                      count: 0,
                      label,
                    }))
                    .filter(
                      (sanitizedLabel) =>
                        !typeAheadLabels.some(
                          (item) => item.label === sanitizedLabel.label,
                        ),
                    ),
                ].filter((item) => !tags.includes(item.label)); // remove any labels from results that are already tagged;
              })()}
            />
            <DefaultIconButton
              aria-label='Add label'
              iconColor={
                isDisabled ? theme.colors.gray2 : theme.colors.aliases.primary
              }
              iconName='add'
              isDisabled={isDisabled}
              onClick={() => {
                createTag(searchQuery);
                searchRef.current?.setSearchValue('');
              }}
              trackingId={UIATrackingIds.LABELS_CREATE_TAG}
            />
          </Search>
        ) : (
          <Warning type='warning'>
            You only have permission to view labels on this content.
          </Warning>
        )}
      </TagWrapper>
      {hasError && (
        <Alert type='warning'>
          Labels can only contain letters, numbers, dashes (-), or underscores
          (_). e.g. flat_lay1
        </Alert>
      )}
      <MetatagText>
        {tags.length === 0 && 'No'}&nbsp;
        {pluralize(tags.length, 'label', undefined, tags.length > 0)}&nbsp;
        added
      </MetatagText>
      <TagsContainer>
        {tags.map((tag, index) => (
          <Label
            isDisabled={!hasPermission || isPending}
            key={`${index}-${tag}`}
            label={tag}
            maxLength={MAX_LABEL_LENGTH}
            maxTooltipWidth={`calc(${theme.mediumMeasure} - ${theme.spacings[6]})`}
            onRemove={() => {
              if (hasPermission && !isPending) {
                const previousTags = tags;
                const remainingTags = tags.filter((item) => item !== tag);
                setTags(remainingTags);
                trackEvent({
                  cuid,
                  event: MediaTrackingEventName.LabelRemoved,
                });
                mutate(
                  {
                    cuid,
                    isModeration: isModeratorUiaEnabled,
                    labelsToRemove: [tag],
                    request: { tags: remainingTags },
                  },
                  { onError: () => setTags(previousTags) },
                );
              }
            }}
          />
        ))}
      </TagsContainer>
    </Tagger>
  );
};

const Tagger = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
    padding: ${theme.spacings[3]};
  `,
);

const LabelWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacings[2]};
`,
);

const TagWrapper = styled.div(
  () => `
    position: relative;
  `,
);

const Warning = styled(Alert)(
  ({ theme }) => `
    margin: ${theme.spacings[0]};
  `,
);

const Search = styled.div(
  ({ theme }) => `
    display: flex;
    gap: ${theme.spacings[2]};
  `,
);

const TagsContainer = styled.div(
  () => `
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacings[2]};
  `,
);
